import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users, Plus, Edit, Trash2, Eye, EyeOff, KeyRound,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { API } from './constants';

export const ClientsTab = ({ clients, setClients, showModal, setShowModal, editingItem, setEditingItem, fetchAllData }) => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Sync form to editing client when modal opens for edit
  useEffect(() => {
    if (editingItem) {
      setFormData({ name: editingItem.name || '', email: editingItem.email || '', password: '' });
    } else {
      setFormData({ name: '', email: '', password: '' });
    }
  }, [editingItem]);

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ name: '', email: '', password: '' });
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        const payload = { name: formData.name, email: formData.email };
        if (formData.password) payload.password = formData.password;
        await axios.put(`${API}/admin/clients/${editingItem.user_id}`, payload, { withCredentials: true });
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/admin/clients`, formData, { withCredentials: true });
        toast.success('Client created successfully');
      }
      closeModal();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Delete client "${client.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/clients/${client.user_id}`, { withCredentials: true });
      toast.success('Client deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleSendReset = async (client) => {
    if (!window.confirm(`Email a password reset link to ${client.email}?`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/clients/${client.user_id}/send-reset`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message || 'Reset email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl text-white">Clients</h2>
        <button
          onClick={() => { setEditingItem(null); setShowModal(true); }}
          className="flex items-center gap-2 px-6 py-3 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors"
          data-testid="add-client-btn"
        >
          <Plus size={16} /> Add Client
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
          <Users className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
          <p className="text-white/60">No clients yet</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <div key={client.user_id} className="bg-[#0a0a0a] border border-white/10 p-6 group" data-testid={`client-${client.user_id}`}>
              <div className="flex items-center gap-4 mb-4">
                {client.picture ? (
                  <img src={client.picture} alt={client.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <span className="text-[#D4AF37] font-heading text-lg">
                      {client.name?.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{client.name}</p>
                  <p className="text-white/60 text-sm truncate">{client.email}</p>
                </div>
              </div>
              <p className="text-white/40 text-xs mb-4">
                Joined {format(new Date(client.created_at), 'MMM d, yyyy')}
              </p>
              <div className="flex items-center gap-2 pt-4 border-t border-white/5">
                <button
                  onClick={() => { setEditingItem(client); setShowModal(true); }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] border border-white/10 transition-colors"
                  data-testid={`client-edit-${client.user_id}`}
                >
                  <Edit className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => handleSendReset(client)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs uppercase tracking-widest border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 transition-colors"
                  data-testid={`client-reset-${client.user_id}`}
                  title="Send password reset email"
                >
                  <KeyRound className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleDelete(client)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-red-400/80 hover:text-red-400 text-xs uppercase tracking-widest border border-red-400/20 hover:border-red-400/50 transition-colors"
                  data-testid={`client-delete-${client.user_id}`}
                  title="Delete client"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Client Modal */}
      <Dialog open={showModal} onOpenChange={(v) => { if (!v) closeModal(); }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">
              {editingItem ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="Client name"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="client@email.com"
                data-testid="client-email-input"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">
                {editingItem ? 'New Password (leave blank to keep current)' : 'Password'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required={!editingItem}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors pr-10"
                  placeholder={editingItem ? 'Leave blank to keep current' : 'Temporary password'}
                  data-testid="client-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#B8AEA1]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" className="w-full py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors" data-testid="create-client-btn">
              {editingItem ? 'Save Changes' : 'Create Client'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
